import { formatBrandName } from "@/features/dashboard/lib/format"
import type { CompetitorCreativeCardModel } from "./drilldown-card.types"
import { resolveDupBadgeCount } from "./drilldown-card-format"
import {
  NO_AD_LIB_URL,
  buildAdLibUrl,
  buildDrilldownRowKey,
  deriveDrilldownCompetitorMedia,
  extractNumber,
  extractString,
  linkableAdId,
} from "./drilldown-row"

/**
 * Maps a raw Competitors BQ row into the redesigned card's view model.
 * `daysPerAsset` is read directly from a backend-computed, NULLIF-guarded, pre-rounded
 * column (services/workspace/src/bq-queries/drilldown.sql.ts) — never recomputed as
 * `days / assets` on the client.
 */
export function mapCompetitorCardRow(
  row: Record<string, unknown>,
  index = 0,
  competitorsPath?: string,
): CompetitorCreativeCardModel {
  const adId = extractString(row.ad_id)
  const media = deriveDrilldownCompetitorMedia(row, competitorsPath)
  const assets = extractNumber(row.Assets) ?? 0
  const ads = extractNumber(row.Ads) ?? 0

  return {
    id: buildDrilldownRowKey(row, index),
    hash: media.hash,
    competitor: media.competitor,
    brandName: formatBrandName(media.competitor),
    logoUrl: media.logoUrl,
    isVideo: media.isVideo,
    isCarousel: media.isCarousel,
    thumbnailUrl: media.thumbnailUrl,
    // Carousel items: empty assetUrl so the single-asset lightbox won't fire;
    // the carousel lightbox is activated via the slides prop instead.
    assetUrl: media.isCarousel ? "" : media.assetUrl,
    carouselSlides: media.carouselSlides ?? undefined,
    adLibUrl:
      ads === 1 && linkableAdId(adId)
        ? buildAdLibUrl(adId, extractString(row.source))
        : NO_AD_LIB_URL,
    days: extractNumber(row.Days) ?? 0,
    assets,
    daysPerAsset: extractNumber(row.DaysPerAsset) ?? 0,
    dupCount: resolveDupBadgeCount(assets),
    minDate: extractString(row.min_date),
    maxDate: extractString(row.max_date),
    effectiveStatus: extractString(row.effective_status).toLowerCase(),
    industry: extractString(row.industry),
    adId: ads === 1 && linkableAdId(adId) ? adId : null,
  }
}
